module Meevo.Components.Common {

    export interface IRoundingService {
        normalizeNumber(value: number, decimalPositions?: number): number;
        denormalizeNumber(value: number): number;
        normalizeDecimalPlaces(value: number, decimalPositions?: number);
        subtractNormalizedNumbers(value1: number, value2: number): number;
        addNormalizedNumbers(value1: number, value2: number): number;
        multiplyNormalizedNumbers(value1: number, value2: number): number;
        divideNormalizedNumbers(value1: number, value2: number): number;
        performOperationOnNormalizedNumbers(value1: number, value2: number, op: Function): number;
        subtractNormalizedNumbersSpread(...values: number[]): number;
        addNormalizedNumbersSpread(...values: number[]): number;
        performOperationOnNormalizedNumbersSpread(op: Function, values: number[]): number;
        cashRoundingInterval(financialInfo: Meevo.Model.Tax, value: number): number;
        greatestCommonDenominator(a: number, b: number): number;
    }

    export class RoundingService implements IRoundingService {
        protected decimalPositions: number;
        protected normalize: string;
        protected normalizeDecimal: string;

        constructor(
            protected identityService: Meevo.Components.Common.IIdentityService
        ) {
            let identity = identityService.current();
            this.decimalPositions = identity.decimalPositions || 2;
            this.normalize = "e" + this.decimalPositions;
            this.normalizeDecimal = "e-" + this.decimalPositions;

            if (!Math.trunc) {
                Math.trunc = function (v) {
                    return v < 0 ? Math.ceil(v) : Math.floor(v);
                };
            }
        }

        normalizeNumber(value: number, decimalPositions?: number): number {
            let multiplier = this.normalize;
            if (typeof decimalPositions !== 'undefined' && decimalPositions !== null) {
                if (decimalPositions < 0) {
                    throw new Error("decimalPositions must be a positive number");
                }
                multiplier = "e" + decimalPositions;
            }
            return +(+(value + multiplier)).toFixed(0);
        }

        denormalizeNumber(value: number): number {
            return +(value + this.normalizeDecimal);
        }

        normalizeDecimalPlaces(value: number, decimalPositions?: number): number {
            let multiplier = this.normalizeDecimal;
            if (typeof decimalPositions !== 'undefined' && decimalPositions !== null) {
                if (decimalPositions < 0) {
                    throw new Error("decimalPositions must be a positive number");
                }
                multiplier = "e-" + decimalPositions;
            }
            return +(this.normalizeNumber(value, decimalPositions) + multiplier);
        }

        subtractNormalizedNumbers(value1: number, value2: number): number {
            return this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a - b);
        }

        addNormalizedNumbers(value1: number, value2: number): number {
            return this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a + b);
        }

        multiplyNormalizedNumbers(value1: number, value2: number): number {
            return +(this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a * b) + this.normalizeDecimal);
        }

        divideNormalizedNumbers(value1: number, value2: number): number {
            return (this.normalizeNumber(value1) / this.normalizeNumber(value2));
        }

        performOperationOnNormalizedNumbers(value1: number, value2: number, op: Function): number {
            return +(op(this.normalizeNumber(value1), this.normalizeNumber(value2)) + this.normalizeDecimal);
        }

        subtractNormalizedNumbersSpread(...values: number[]): number {
            return this.performOperationOnNormalizedNumbersSpread((a, b) => a - b, values);
        }

        addNormalizedNumbersSpread(...values: number[]): number {
            return this.performOperationOnNormalizedNumbersSpread((a, b) => a + b, values);
        }

        performOperationOnNormalizedNumbersSpread(op: Function, values: number[]): number {
            if (!values || values.length == 0) {
                return 0;
            }

            var total: number = values[0];

            for (var i = 1; i < values.length; i++) {
                total = +(op(this.normalizeNumber(total), this.normalizeNumber(values[i])) + this.normalizeDecimal);
            }

            return total;
        }

        protected evenRound(num: number, decimalPlaces: number) {
            let d = decimalPlaces || 0;
            let m = Math.pow(10, d);
            let n = +(d ? num * m : num).toFixed(8);
            let i = Math.floor(n), f = n - i;
            let e = 1e-8;
            let r = (f > 0.5 - e && f < 0.5 + e)
                ? ((i % 2 == 0) ? i : i + 1)
                : Math.round(n);

            return d ? r / m : r;
        }

        protected awayFromZeroRound(value: number, decimalPlaces: number) {
            if (value === 0) {
                return value;
            }

            let multiplier = Math.pow(10, decimalPlaces || 0);
            let absoluteValue = Math.abs(value);
            let sign = value / absoluteValue;
            return sign * Math.round(absoluteValue * multiplier) / multiplier;
        }

        protected getDecimalPlaces(financialInfo: Meevo.Model.Tax) {
            if (this.decimalPositions) {
                return this.decimalPositions;
            }

            switch (financialInfo.decimalPlaceRounding)
            {
                case Meevo.Model.DecimalRoundingPlaceEnum.Tenths:
                    return 1;
                case Meevo.Model.DecimalRoundingPlaceEnum.Thousandths:
                    return 3;
                case Meevo.Model.DecimalRoundingPlaceEnum.TenThousandths:
                    return 4;
                default:
                    return 2;
            }
        }

        meevoRound(financialInfo: Meevo.Model.Tax, value: number) {
            let decimals = this.getDecimalPlaces(financialInfo);
            let roundedValue = 0;

            switch (financialInfo.roundingMode) {
                case Meevo.Model.RoundingModeEnum.HalfUp:
                    roundedValue = this.awayFromZeroRound(value, decimals);
                    break;

                case Meevo.Model.RoundingModeEnum.HalfEven:
                    roundedValue = this.evenRound(value, decimals);
                    break;
            }

            return this.normalizeDecimalPlaces(roundedValue);
        }

        cashRoundingInterval(financialInfo: Meevo.Model.Tax, value: number): number {
            let interval = this.normalizeNumber(financialInfo.cashRoundingInterval);

            switch (financialInfo.cashRoundingMode) {
                case Meevo.Model.CashRoundingModeEnum.RoundUp:
                case Meevo.Model.CashRoundingModeEnum.RoundDown:
                    const normalizedValue = this.normalizeNumber(value);
                    const remainder = normalizedValue % interval;
                    if (remainder === 0) {
                        return this.normalizeDecimalPlaces(value);
                    }
                    let intervals = Math.trunc(normalizedValue / interval);
                    if (financialInfo.cashRoundingMode === Meevo.Model.CashRoundingModeEnum.RoundUp) {
                        intervals += Math.sign(normalizedValue);
                    }
                    return this.denormalizeNumber(intervals * interval);

                default:
                    let integral = Math.trunc(value);
                    let fractional = value - integral;
                    let roundedValue = this.normalizeDecimalPlaces(fractional) / interval;

                    return this.normalizeDecimalPlaces(this.meevoRound(financialInfo, roundedValue) * interval + integral);
            }
        }

        greatestCommonDenominator(a: number, b: number): number
        {
            return b === 0 ? a : this.greatestCommonDenominator(b, a % b);
        }

        static factory(): Function {
            const svc = (
                identityService
            ) => new RoundingService(
                identityService
            );

            svc.$inject = [
                'meevo.components.common.IdentityService',
            ];

            return svc;
        }
    }

    angular.module("meevo.components.common")
        .factory("meevo.components.common.RoundingService", RoundingService.factory());
}